import { onBeforeUnmount, ref, watch } from 'vue';
import type { Ref } from 'vue';

type UseIncrementalRenderingOptions = {
  /** The reactive data source to watch for changes */
  dataSource: Ref<unknown[]>;
  /** Initial number of items to show */
  initialBatchSize?: number;
  /** Number of items to add in each subsequent batch */
  batchSize?: number;
};

/**
 * Composable for incrementally rendering large lists to avoid blocking the UI.
 *
 * Instead of rendering all items at once, this composable renders items in batches,
 * which prevents the browser from freezing when displaying large datasets.
 *
 * @example
 * ```ts
 * const { visibleCount, startBatchRendering } = useIncrementalRendering({
 *   dataSource: computed(() => props.items),
 *   initialBatchSize: 20,
 *   batchSize: 20,
 * });
 *
 * const visibleItems = computed(() => props.items.slice(0, visibleCount.value));
 * ```
 */
export default function useIncrementalRendering({
  dataSource,
  initialBatchSize = 20,
  batchSize = 20,
}: UseIncrementalRenderingOptions) {
  const visibleCount = ref(initialBatchSize);
  const pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  /**
   * Clears all pending timeouts to prevent memory leaks
   */
  function clearPendingTimeouts() {
    pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    pendingTimeouts.clear();
  }

  /**
   * Incrementally renders batches of items to avoid blocking the UI
   */
  function startBatchRendering() {
    // Clear any existing timeouts before starting a new batch
    clearPendingTimeouts();

    function renderNextBatch() {
      if (visibleCount.value >= dataSource.value.length) return;

      visibleCount.value = Math.min(
        visibleCount.value + batchSize,
        dataSource.value.length,
      );

      if (visibleCount.value < dataSource.value.length) {
        const timeoutId = setTimeout(renderNextBatch, 0);
        pendingTimeouts.add(timeoutId);
      }
    }

    const timeoutId = setTimeout(renderNextBatch, 0);
    pendingTimeouts.add(timeoutId);
  }

  // Watch data source and restart batch rendering when it changes
  // The immediate: true flag ensures this runs once during setup,
  // eliminating the need for a separate onMounted call
  watch(
    dataSource,
    (newData) => {
      // Reset visible count to initial batch size
      visibleCount.value = Math.min(initialBatchSize, newData.length);

      // Restart batch rendering after data changes
      startBatchRendering();
    },
    { immediate: true },
  );

  // Clean up pending timeouts when component unmounts to prevent memory leaks
  onBeforeUnmount(() => {
    clearPendingTimeouts();
  });

  return {
    visibleCount,
    startBatchRendering,
  };
}
